import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SearchInput } from "../components/search/SearchInput";
import { SearchResults } from "../components/search/SearchResults";
import { Brain } from "lucide-react";
import { AuraBackground } from "../components/AuraBackground";
import { SearchHeader } from "../components/SearchHeader";
import { useRecordsApi } from "../lib/api/records";
import { useSearchParams } from "react-router-dom";
import type { SearchResponse } from "../types/search";

import intelligentiaLogo from "../images/intelligentiahumana.jpg";

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const page = parseInt(searchParams.get("page") || "0");
  const pageSize = 5;
  const recordsApi = useRecordsApi();

  const { data, isLoading } = useQuery<SearchResponse>({
    queryKey: ["search", query, page],
    queryFn: async () => {
      const response = await recordsApi.searchRecords(query, page, pageSize);
      // Update search ID in URL
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("q", query);
        newParams.set("page", page.toString());
        if (response.search_id) {
          newParams.set("search_id", response.search_id);
        }
        return newParams;
      });
      return response;
    },
    enabled: query.length > 0,
  });

  const handleSearch = (newQuery: string) => {
    // Clear all params and set new query
    setSearchParams({ q: newQuery, page: "0" });
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      // Preserve all existing params
      prev.forEach((value, key) => {
        newParams.set(key, value);
      });
      // Update page
      newParams.set("page", newPage.toString());
      return newParams;
    });
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-b from-[#744796] to-[#48b2cd]">
      <AuraBackground />
      <SearchHeader />
      <div className="relative w-full max-w-7xl px-4">
        <div className="flex flex-col items-center text-center">
          <img
            src={intelligentiaLogo}
            alt="Intelligentia Logo"
            className="w-80 object-contain"
          />

          <p className="mb-8 text-lg text-white/90">
            Accéder à l'intelligence humaine propulsée par l'IA
          </p>

          <div className="w-full max-w-2xl">
            <SearchInput onSearch={handleSearch} defaultValue={query} />
          </div>

          {(query.length > 0 || isLoading) && (
            <SearchResults
              results={data?.results?.flatMap((r) => r.results) || []}
              total={data?.total || 0}
              isLoading={isLoading}
              from={data?.from_ || 0}
              to={data?.to || 0}
              page={page}
              hasNextPage={data?.has_next || false}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
}
